import React, { createContext, useContext, useMemo } from "react";
import { EditingTeamRole, useGetModernizedCourseEditingTeamQuery } from "@axio/graphql/generated/graphql";
import { useAuth } from "../../../../../hooks/useAuth";

export type TeamRole = "faculty" | "instructional-designer" | "observer";

export interface TeamMember {
  id: string;
  name: string;
  role: TeamRole;
  email?: string;
}

const ROLE_MAP: Record<string, TeamRole> = {
  [EditingTeamRole.Instructor]: "faculty",
  [EditingTeamRole.InstructionalDesigner]: "instructional-designer",
  [EditingTeamRole.Observer]: "observer",
};

interface TeamMembersContextValue {
  teamMembers: TeamMember[];
  currentUserId: string;
  currentUserName: string;
}

const TeamMembersContext = createContext<TeamMembersContextValue>({
  teamMembers: [],
  currentUserId: "",
  currentUserName: "",
});

export function useTeamMembers(): TeamMember[] {
  return useContext(TeamMembersContext).teamMembers;
}

export function useCurrentUserId(): string {
  return useContext(TeamMembersContext).currentUserId;
}

export function useCurrentUserName(): string {
  return useContext(TeamMembersContext).currentUserName;
}

export function TeamMembersProvider({ courseId, children }: { courseId: string; children: React.ReactNode }) {
  const { userInfo } = useAuth();
  const { data } = useGetModernizedCourseEditingTeamQuery({
    variables: { courseId },
    fetchPolicy: "cache-and-network",
  });

  const teamMembers = useMemo<TeamMember[]>(() => {
    const members = data?.getModernizedCourseEditingTeam?.members;
    if (!members) return [];
    return members.map((m) => ({
      id: m.userId,
      name: m.name,
      role: ROLE_MAP[m.role] ?? "observer",
      email: m.email ?? undefined,
    }));
  }, [data]);

  const value = useMemo<TeamMembersContextValue>(
    () => ({ teamMembers, currentUserId: userInfo.userId, currentUserName: userInfo.userName }),
    [teamMembers, userInfo.userId, userInfo.userName]
  );

  return <TeamMembersContext.Provider value={value}>{children}</TeamMembersContext.Provider>;
}
